'use client'
import * as Ariakit from '@ariakit/react'
import { isEmpty } from 'lodash-es'
import { Children, forwardRef, isValidElement, useLayoutEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { TypeStyle, timingDefault, useLmfAnalyticsOnClick } from '@syconium/little-miss-figgy'

const StyledTabListWrapper = styled.div`
  overflow-x: auto;
`

const StyledTabList = styled(Ariakit.TabList)<{
  $tabWidth: number
  $tabIndex: number
  $animated: boolean
}>`
  display: flex;
  justify-content: space-between;
  background-color: ${o => o.theme.color.fill.disabled.subtle};
  border-radius: 24px;
  position: relative;
  min-width: fit-content;

  &::before {
    content: '';
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: 3px;
    border-radius: 22px;
    background-color: ${o => o.theme.color.fill.background};
    transition: ${o => (o.$animated ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none')};
    z-index: 0;
    transform: translateX(calc(${o => `${o.$tabIndex * 100}% + ${o.$tabIndex * 6}px`}));
    width: calc(${o => o.$tabWidth}% - 6px);
  }
`

const StyledTab = styled(Ariakit.Tab)`
  flex: 1 1 0%;
  min-width: 100px;
  border-radius: 24px;
  border: 2px solid transparent;
  padding: ${o => o.theme.spacing(2)};
  text-align: center;
  cursor: pointer;
  color: ${o => o.theme.color.stroke.neutral.text.secondary.on.background};
  transition: ${timingDefault} ease-out color;
  position: relative;
  z-index: 1;
  background: transparent;

  &:hover {
    color: ${o => o.theme.color.stroke.neutral.text.primary.on.background};
  }
`

export const PillTabProvider = Ariakit.TabProvider

export interface PillTabListProps extends Ariakit.TabListProps {
  animated?: boolean
}

export const PillTabList = forwardRef<HTMLDivElement, PillTabListProps>(function PillTabList(
  { animated = false, ...props },
  ref
) {
  const [preventAnimation, setPreventAnimation] = useState(true)
  const store = Ariakit.useTabContext()
  const activeId = Ariakit.useStoreState(store, 'selectedId')

  // Tabs only register themselves in a layout effect, so `items` is empty during SSR and on
  // the first client render. Fall back to the children so the indicator is sized and
  // positioned correctly in the initial HTML.
  const ssrAvailableItems = useMemo(
    () =>
      Children.toArray(props.children)
        .filter(isValidElement<{ id?: string }>)
        .map(child => {
          return {
            id: child.props.id,
          }
        }),
    [props.children]
  )

  const clientItems = Ariakit.useStoreState(store, 'items')
  const items = clientItems && !isEmpty(clientItems) ? clientItems : ssrAvailableItems
  const tabWidth = 100 / (items.length || 1)
  const activeIndex = Math.max(
    items.findIndex(item => item.id === activeId),
    0
  )
  useLayoutEffect(() => {
    // prevent animating on load
    const id = requestAnimationFrame(() => setPreventAnimation(false))
    return () => cancelAnimationFrame(id)
  }, [])

  return (
    <StyledTabListWrapper ref={ref}>
      <StyledTabList
        $tabWidth={tabWidth}
        $tabIndex={activeIndex}
        $animated={animated && !preventAnimation}
        {...props}
      />
    </StyledTabListWrapper>
  )
})

export const PillTabPanel = Ariakit.TabPanel

/* An individual tab component, visually displayed as a "pill". Expected to be used as a direct child of `PillTabList`. */
export const PillTab = forwardRef<HTMLButtonElement, Ariakit.TabProps>(function PillTab(
  {
    children,
    render = <TypeStyle.BodyNano fontWeight='700' textTransform='uppercase' isUppercase />,
    ...rest
  },
  ref
) {
  return (
    <StyledTab ref={ref} render={render} {...rest}>
      {children}
    </StyledTab>
  )
})

export const PillTabWithAnalytics = forwardRef<HTMLButtonElement, Ariakit.TabProps>(
  function PillTabWithAnalytics({ onClick, ...rest }, ref) {
    const onClickWithTracking = useLmfAnalyticsOnClick({
      onClick,
      label: undefined,
      variations: [],
      element: 'tab',
      href: undefined,
    })
    return <PillTab ref={ref} onClick={onClickWithTracking} {...rest} />
  }
)
